import React, { useCallback, useMemo } from 'react'

import type { Event } from '@/components/common/EventCalendar'
import EventCalendar from '@/components/common/EventCalendar'
import type { CalendarEventObj } from '@/components/common/EventCalendar/types'
import { DataLoading } from '@/components/dashboard/Loading'

import { COLOR_CLASSNAMES } from '@/constants/color'
import { STATUS_COLOR_CLASSNAMES } from '@/constants/task'

import { getTaskPath } from '@/helpers/task'

import { useSafePush } from '@/hooks/navigation/useSafePush'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { usePursuits } from '@/hooks/pursuits/usePursuits'
import { useUserTasks } from '@/hooks/tasks/useUserTasks'

import type { Pursuit } from '@/types/pursuit/Pursuit'

export const UserTasksCalendar: React.FC = () => {
  const { activeOrgId } = useActiveOrgId()
  const { safePush } = useSafePush()

  // Fetch tasks
  const { data: tasksData, isLoading: isLoadingTasks } = useUserTasks({
    orgId: activeOrgId ?? '',
    pagination: { limit: 1000 },
    sort: { order_by: 'due_date', sort: 'asc' },
    filter: {
      status: ['todo', 'in_progress', 'in_review'],
    },
  })

  // Fetch pursuits with due dates (exclude archived) using selector
  const { data: pursuitsWithDueDate = [], isLoading: isLoadingPursuits } =
    usePursuits<Pursuit[]>({
      orgId: activeOrgId ?? '',
      selector: (pursuits) =>
        pursuits.filter(
          (pursuit) => pursuit.due_date != null && !pursuit.archived,
        ),
    })

  const isLoading = isLoadingTasks || isLoadingPursuits

  // Combine into calendar events
  const events: Array<Event> = useMemo(() => {
    const taskEvents: Array<Event> = (tasksData?.data ?? []).flatMap((task) => {
      if (task.due_date === null) return []
      return [
        {
          title: task.title,
          start: task.due_date,
          end: task.due_date,
          obj: { type: 'task', data: task } as CalendarEventObj,
          className: STATUS_COLOR_CLASSNAMES[task.status],
        },
      ]
    })

    const pursuitEvents: Array<Event> = pursuitsWithDueDate.map((pursuit) => ({
      title: pursuit.title,
      start: pursuit.due_date,
      end: pursuit.due_date,
      obj: { type: 'pursuit', data: pursuit } as CalendarEventObj,
      // Use the stage color for styling
      className: pursuit.stage?.color
        ? COLOR_CLASSNAMES[pursuit.stage.color]
        : COLOR_CLASSNAMES['zinc-light'],
    }))

    return [...taskEvents, ...pursuitEvents]
  }, [tasksData?.data, pursuitsWithDueDate])

  const onEventClick = useCallback(
    (event: Event) => {
      const eventObj = event.obj as CalendarEventObj
      if (!activeOrgId) return

      if (eventObj.type === 'task') {
        const path = getTaskPath(activeOrgId, eventObj.data)
        if (path) {
          safePush(path)
        }
      } else if (eventObj.type === 'pursuit') {
        const pursuit = eventObj.data
        safePush(`/orgs/${activeOrgId}/pursuits/${pursuit.id}`)
      }
    },
    [activeOrgId, safePush],
  )

  if (isLoading || !activeOrgId) {
    return (
      <div className="w-full h-[500px] rounded-lg border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-900">
        <DataLoading />
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <div className="w-full h-[300px] rounded-lg border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-900 flex items-center justify-center">
        <p className="text-primary-500 dark:text-primary-400 text-sm">
          No tasks or pursuits with due dates found
        </p>
      </div>
    )
  }

  return (
    <div className="w-full h-[500px] rounded-lg border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-900 p-4">
      <EventCalendar events={events} onEventClick={onEventClick} />
    </div>
  )
}

export default UserTasksCalendar
